import {
  Component,
  input,
  output,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Optional,
  Inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { OriBadgeComponent } from "~/components/ui/badge";
import { OVERLAY_DATA, OverlayService } from "~/components/overlay.service";
import { LucideAngularModule, ChevronDown } from "lucide-angular";
import { DynamicOverlayConfig } from "~/components/overlay.service";
import { OriDropdownMenuContent2, OriDropdownMenuItem2 } from "~/components/ui/dropdown-menu";
import { OverlayRef } from "@angular/cdk/overlay";

@Component({
  standalone: true,
  imports: [OriBadgeComponent, FormsModule, LucideAngularModule],
  template: `
    <ori-badge
      #triggerElement
      (click)="openDropdown()"
      class="flex select-none rounded-md cursor-pointer items-center justify-between px-2"
      [class.text-white]="selectedValue !== null"
      [class.bg-black]="selectedValue !== null"
      [class.text-gray-500]="selectedValue === null"
      variant="outline"
    >
      <span class="truncate">
        {{ selectedValue || 'Select' }}
      </span>
      <lucide-angular
        [img]="ChevronDown"
        size="14"
        strokeWidth="2"
        class="ml-1 flex-shrink-0"
      />
    </ori-badge>
  `,
})
export class DropdownCellComponent implements OnInit, OnDestroy {
  readonly value = input<unknown>();
  readonly options = input<string[]>([]);
  readonly change = output<unknown>();
  readonly blur = output<unknown>();

  selectedValue: string | null = null;
  private initialValue: string | null = null;

  @ViewChild('triggerElement', { read: ElementRef })
  triggerElement!: ElementRef;

  readonly ChevronDown = ChevronDown;

  private isScrolling = false;
  private scrollTimeout: any;
  private scrollListeners: (() => void)[] = [];

  constructor(private overlayService: OverlayService) {}

  ngOnInit() {
    const val = this.value() as string;
    this.selectedValue = this.checkOptions(val) ? val : null;
    this.initialValue = this.selectedValue;

    setTimeout(() => this.setupScrollTracking(), 0);
  }

  ngOnDestroy() {
    this.cleanupScrollListeners();
    clearTimeout(this.scrollTimeout);
  }

  private setupScrollTracking() {
    if (!this.triggerElement?.nativeElement) return;

    const scrollableParents = this.findScrollableParents(this.triggerElement.nativeElement);
    scrollableParents.push(window as any);

    scrollableParents.forEach(parent => {
      const scrollHandler = () => {
        this.isScrolling = true;
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 150);
      };

      parent.addEventListener('scroll', scrollHandler, { passive: true });
      this.scrollListeners.push(() => parent.removeEventListener('scroll', scrollHandler));
    });
  }

  private findScrollableParents(element: HTMLElement): HTMLElement[] {
    const scrollableElements: HTMLElement[] = [];
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
      const style = getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflow + style.overflowX + style.overflowY)) {
        scrollableElements.push(parent);
      }
      parent = parent.parentElement;
    }

    return scrollableElements;
  }

  private cleanupScrollListeners() {
    this.scrollListeners.forEach(dispose => dispose());
    this.scrollListeners = [];
  }

  openDropdown() {
    if (!this.isTriggerVisible()) return;

    if (!this.isScrolling) {
      this.performOpenDropdown();
    } else {
      this.overlayService.closeAll();
    }

    // if (this.isScrolling) {
    //   const checkAndOpen = () => {
    //     if (!this.isScrolling && this.isTriggerVisible()) {
    //       this.performOpenDropdown();
    //     } else {
    //       setTimeout(checkAndOpen, 50);
    //     }
    //   };
    //   setTimeout(checkAndOpen, 50);
    //   return;
    // }

    // this.performOpenDropdown();
  }

  private isTriggerVisible(): boolean {
    const element = this.triggerElement?.nativeElement;
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth &&
      style.visibility !== 'hidden' &&
      style.display !== 'none'
    );
  }

  private performOpenDropdown() {
    if (!this.isTriggerVisible()) return;

    const overlayConfig: DynamicOverlayConfig = {
      relativeTo: this.triggerElement,
      offsetY: 4,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: 'reposition',
      panelClass: 'dropdown-options-panel',
      width: this.triggerElement.nativeElement.offsetWidth,
      maxHeight: '200px',
    };

    this.overlayService.open(DropdownOptionsComponent, overlayConfig, {
      options: this.options(),
      selectedValue: this.selectedValue,
      onSelect: (val: string | null) => this.onOptionSelected(val),
    });
  }

  private onOptionSelected(value: string | null) {
    this.selectedValue = value;
    if (this.selectedValue !== this.initialValue) {
      this.blur.emit(this.selectedValue ?? '');
      this.initialValue = this.selectedValue;
    }
  }

  private checkOptions(value: string): boolean {
    return !!value && this.options().includes(value);
  }
}






@Component({
  selector: 'app-dropdown-options',
  standalone: true,
  imports: [LucideAngularModule, OriDropdownMenuContent2, OriDropdownMenuItem2],
  template: `
    <div
      #dropdownContent
      oriDropdownMenuContent2
      tabindex="0"
      class="transform origin-top"
      [class]="animationClasses"
      (click)="onBackdropClick($event)"
    >
      <div
        oriDropdownMenuItem2
        class="hover:bg-blue-50 transition-colors duration-150"
        [class.selected]="data?.selectedValue === null"
        (click)="selectOption(null)"
      >
        <span class="text-gray-500">Select</span>
      </div>

      @for (option of data?.options || []; track option) {
        <div
          oriDropdownMenuItem2
          class="hover:bg-blue-50 transition-colors duration-150"
          [class.selected]="data?.selectedValue === option"
          (click)="selectOption(option)"
        >
          {{ option }}
        </div>
      }
    </div>
  `
})
class DropdownOptionsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dropdownContent') dropdownContent!: ElementRef;
  isVisible = false;
  isClosing = false;
  private animationTimeout?: number;

  constructor(
    @Optional() private overlayRef: OverlayRef,
    @Optional() @Inject(OVERLAY_DATA) public data: any
  ) {
    this.overlayRef?.keydownEvents().subscribe((event) => this.handleKeydown(event));
    this.overlayRef?.outsidePointerEvents().subscribe((event) => {
      event.preventDefault?.();
      event.stopPropagation?.();
      this.closeDropdownWithAnimation();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => (this.isVisible = true), 10);
  }

  ngOnDestroy() {
    clearTimeout(this.animationTimeout);
  }

  get animationClasses(): string {
    if (this.isClosing) {
      return 'transition-all duration-200 ease-in transform origin-top opacity-0 scale-90 -translate-y-3';
    }
    return this.isVisible
      ? 'transition-all duration-200 ease-out transform origin-top opacity-100 scale-100 translate-y-0'
      : 'transition-all duration-200 ease-out transform origin-top opacity-0 scale-95 -translate-y-2';
  }

  onBackdropClick(event: Event) {
    event.stopPropagation();
  }

  selectOption(value: string | null) {
    this.data?.onSelect?.(value);
    this.closeDropdownWithAnimation();
  }

  private closeDropdownWithAnimation() {
    if (this.isClosing) return;

    this.isClosing = true;
    this.isVisible = false;

    this.animationTimeout = window.setTimeout(() => {
      this.overlayRef?.dispose();
    }, 200);
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeDropdownWithAnimation();
    } else {
      event.preventDefault?.();
      event.stopPropagation?.();
      // this.closeDropdownWithAnimation(); 
    }
  }
}
